import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BlocklyDbType } from '@shared/index';
import * as Blockly from 'blockly';
import { firstValueFrom } from 'rxjs';
import { BlocklyHelperService, BlocklyNesting } from './shared/components/blockly/blockly-editor/blockly-helper.service';
import { RouteShareService } from './shared/services/route-share.service';

// Types
export interface PluginBlock {
  name: string;
  blocks: string[];
}


export type PluginBlockFileContent = {
  ID: string,
  block: string,
  code: string,
  features: string,
  nestings: string
};

export interface PluginsResponse {
  plugins: string[];
}

type ToolboxItemInfo = Blockly.utils.toolbox.ToolboxItemInfo;


@Injectable({
  providedIn: 'root'
})
export class BlocklyPluginsService {
  public pluginIdToNameMap: Map<string, string>;
  public pluginIdToBlockIdsMap: Map<string, string[]>;
  constructor(private http: HttpClient, private rss: RouteShareService, private BH: BlocklyHelperService, private toastr: ToastrService) {
    this.pluginIdToNameMap = new Map();
    this.pluginIdToBlockIdsMap = new Map();
  }

  public pluginUsage(pluginId: string, stages: BlocklyDbType[]) {
    let counter = 0;
    stages.forEach(stage => {
      const blockIds = this.pluginIdToBlockIdsMap.get(pluginId);
      blockIds?.forEach(blockId => {
        if (stage.xml.includes(blockId)) {
          counter++;
        }
      });


    });

    //@ts-ignore
    // console.log("STAGES 123", stages[0].xml.includes(key));
    return counter;

  }

  public pluginIsValid(pluginTXT: string) {
    if (pluginTXT.trim() == "") {
      this.toastr.error('GENERAL', $localize`:@@empty-plugin-code:Bitte gib einen Plugin-Code ein.`, { timeOut: 10000 });
      return false;
    }

    if (!pluginTXT.startsWith("@plugin:")) {
      this.toastr.error('GENERAL', $localize`:@@plugin-code-must-start-with:Der Plugin-Code muss mit '@plugin:' beginnen.`, { timeOut: 10000 });
      return false;
    }


    return true;
  }





  public setPluginIdToName(pluginId: string, pluginName: string): void {
    this.pluginIdToNameMap.set(pluginId, pluginName);
  }

  public getPluginNameById(pluginId: string): string | undefined {
    return this.pluginIdToNameMap.get(pluginId);
  }


  public async removePluginById(pluginId: string): Promise<void> {
    await firstValueFrom(this.http.post(`/api/admin/plugin/remove/${this.rss.botConfig._id}/${pluginId}`, {}));
    this.pluginIdToNameMap.delete(pluginId);
    this.pluginIdToBlockIdsMap.delete(pluginId);
  }

  public async addPlugin(pluginTXT: string, pluginName: string): Promise<void> {
    const botId = this.rss.botConfig._id;
    const addCallback = await firstValueFrom(this.http.post(`/api/admin/plugin/add/${botId}`, pluginTXT));
    //@ts-ignore
    this.setPluginIdToName(addCallback["_id"], pluginName);
    //@ts-ignore
    this.pluginIdToBlockIdsMap.set(addCallback["_id"], this.extractBlockIdsFromPluginTxt(pluginTXT));
  }

  public async updatePlugin(pluginId: string, pluginTXT: string, pluginName: string): Promise<void> {
    const botId = this.rss.botConfig._id;
    await firstValueFrom(this.http.post(`/api/admin/plugin/update/${botId}/${pluginId}`, pluginTXT));
    this.setPluginIdToName(pluginId, pluginName);
    this.pluginIdToBlockIdsMap.set(pluginId, this.extractBlockIdsFromPluginTxt(pluginTXT));
  }

  public extractBlockIdsFromPluginTxt(pluginTXT: string): string[] {
    return pluginTXT.split("###BLOCKDEF").map(block => {
      const idMatch = block.match(/#::ID\s+(\S+)/);
      return idMatch ? idMatch[1] : null;
    }).filter(id => id !== null) as string[];
  }

  public pluginNameisTaken(pluginName: string, excludePluginId?: string | null): boolean {
    for (const [id, name] of this.pluginIdToNameMap.entries()) {
      if (id === excludePluginId) continue;
      if (name === pluginName) {
        return true;
      }
    }
    return false;
  }

  public getPluginName(pluginTXT: string, excludePluginId?: string | null): string {
    // Get the first row (line) from pluginTXT
    //@ts-ignore
    const firstRow = pluginTXT.split('\n')[0].trim();
    let baseName = firstRow.startsWith("@plugin:") ? firstRow.substring(8).trim() : firstRow;

    let name = baseName;
    let counter = 2;
    while (this.pluginNameisTaken(name, excludePluginId)) {
      name = `${baseName} (${counter})`;
      counter++;
    }
    return name;
  }


  public getRawBlocks(pluginTXT: string): string[] {

    let blocks = pluginTXT.split("###BLOCKDEF");
    // Remove blocks that start with '@plugin' (after trimming whitespace)
    blocks = blocks.filter(block => !block.trim().startsWith('@plugin'));
    blocks = blocks.map(block => block.trim());
    return blocks;
  }

  public async getPlugins() {
    const botId = this.rss.botConfig._id;
    const r = await firstValueFrom(this.http.get<PluginsResponse>(`/api/admin/plugin/get/plugins/${botId}`));
    this.pluginIdToNameMap.clear();
    this.pluginIdToBlockIdsMap.clear();

    return r;
  }



  /**
   *
   * myvar:DUMMY_TEXT_TYPE -  text="Y"
   */
  private _parseNesting(blockData: PluginBlockFileContent): BlocklyNesting[] {
    const data = blockData.nestings;
    if (!data) return [];


    return data.split('\n').reduce((acc: BlocklyNesting[], line: string) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return acc;

      // Regex: Trennung von forValue, type/targetBlockType und dem Default-Wert
      const match = trimmedLine.match(/^-([^:]+):([^|]+)\|(.*)/);

      if (match) {
        const [_, forValue, type, rawValue] = match;

        // Typ-Konvertierung für targetBlockDefaults
        //@ts-ignore
        let parsedValue: string | number | boolean = rawValue;
        if (type === 'NUMBER') parsedValue = Number(rawValue);
        //@ts-ignore
        if (type === 'BOOLEAN') parsedValue = rawValue.toLowerCase() === 'true';

        // Mapping anwenden, falls vorhanden, sonst type selbst verwenden

        // Mapping von type zu targetBlockType
        const typeToBlockType: Record<string, string> = {
          'TEXT': 'DUMMY_STRING',
          'NUMBER': 'DUMMY_NUMBER',
          'BOOLEAN': 'DUMMY_BOOLEAN',
        };
        const typeToBlockDefaults: Record<string, string> = {
          'TEXT': 'text',
          'NUMBER': 'number',
          'BOOLEAN': 'boolean',
        };

        //@ts-ignore
        const targetBlockType = typeToBlockType[type] || type;
        //@ts-ignore
        const targetBlockDefaultsKey = typeToBlockDefaults[type] || type.toLowerCase();

        const nesting: BlocklyNesting = {
          forValue: forValue + "",
          isShadow: true, // Standardmäßig auf true, falls nicht anders spezifiziert
          targetBlockType: targetBlockType,
          targetBlockDefaults: {
            [targetBlockDefaultsKey]: parsedValue
          }
        };

        acc.push(nesting);
      }
      console.log("Parsed nestings:", acc);
      return acc;
    }, []);
  }

  public addBlockUniqueIdsToPluginTxt(pluginTXT: string): string {
    // 1. Den Text in einzelne Blöcke splitten
    const blocks = pluginTXT.split('###BLOCKDEF').filter(block => block.trim() !== "");

    const processedBlocks = blocks.map((block, index) => {
      let cleanBlock = block.trim();

      // Prüfung: Existiert bereits eine ID?
      // Sucht nach dem Muster #::ID gefolgt von der nächsten Zeile
      const hasId = /^#::ID/m.test(cleanBlock);

      if (!hasId) {
        // Generiere eine neue ID (z.B. basierend auf dem Index oder Zeitstempel)
        const generatedId = `GENERATED_ID_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

        // Füge die ID am Anfang des Blocks ein
        cleanBlock = `#::ID \n${generatedId}\n${cleanBlock}`;
      }

      return `###BLOCKDEF\n${cleanBlock}`;
    });

    // 2. Wieder zu einem Gesamtstring zusammenfügen
    const fixed = processedBlocks.join('\n\n');
    const parts = fixed.split("@plugin:");
    return "@plugin:" + parts[1];


  }


  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // USE addBlockUniqueIdsToPluginTxt BEFORE
  public registerPluginByTxt(fixedPluginTXT: string, excludePluginId?: string | null): ToolboxItemInfo | null {
    const rawBlocks = this.getRawBlocks(fixedPluginTXT);
    const name = this.getPluginName(fixedPluginTXT, excludePluginId);
    //const failed = true;
    /* if (failed) {
      return null;

    } */



    const blocksArray: ToolboxItemInfo[] = [];
    rawBlocks.forEach(blockText => {
      const blockData = this.parseBlockFileToObject(blockText);
      if (!blockData.ID) {
        this.toastr.error('GENERAL', $localize`:@@plugin-block-missing-id:Ein Block in diesem Plugin hat keine ID. Bitte füge eine Zeile mit '#::ID' hinzu, um diesem Block eine eindeutige ID zu geben. Block-Text: ${blockText}`);
        return;
      }


      blocksArray.push(this.BH._fromPlugin(name, blockData, this._parseNesting(blockData)));

    });

    return {
      name: name,
      kind: "category",
      contents: blocksArray

    };
  }

  public async fetchPluginByPath(pluginId: string) {
    // const org = this.getCurrentOrg();
    const botId = this.rss.botConfig._id;
    const url = `/api/admin/plugin/get/pluginfile/${botId}/${pluginId}`;
    return firstValueFrom(this.http.get(url, { responseType: 'text' }));
  }

  public parseBlockFileToObject(text: string): PluginBlockFileContent {
    const lines = text.split('\n');
    const result = {};
    //@ts-ignore
    let currentKey = null;

    lines.forEach(line => {
      // Prüfen, ob die Zeile mit #: beginnt
      if (line.startsWith('#::')) {
        // Key extrahieren (alles nach #:)
        currentKey = line.substring(3).trim();
        //@ts-ignore
        result[currentKey] = "";
        //@ts-ignore
      } else if (currentKey) {
        // Inhalt zum aktuellen Key hinzufügen (mit Trimming für Sauberkeit)
        const content = line.trim();
        if (content) {
          // Falls bereits Text vorhanden ist, mit Zeilenumbruch trennen
          //@ts-ignore
          result[currentKey] += (result[currentKey] === "" ? "" : "\n") + content;
        }
      }
    });

    return result as PluginBlockFileContent;
  }


}
